import { Button } from "@/design-system/Button/Button";
import { CelebrationDialog } from "@/design-system/CelebrationDialog/CelebrationDialog";
import { CheckBox } from "@/design-system/Check/Check";
import PlayerCardCondensed from "@/design-system/PlayerCard/PlayerCardCondensed";
import { ProgressBar } from "@/design-system/ProgressBar/ProgressBar";
import { ContentTag } from "@/design-system/Tags/ContentTag/ContentTag";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { MetaRatingUpgradeArrow } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { EmptySlotCard } from "@/features/MyClub/ActiveSquad/EmptySlotCard";
import { SetupDialog } from "@/features/MyClub/Layout/SetupDialog";
import { extractRewardUrl } from "@/functions/extractRewardUrl";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import { tierSelector } from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, setOnboardingStep } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ONBOARDING_STEP_FEATURES } from "@repo/shared/myClub";
import {
  bestSquadSelector,
  buildBestSquad,
  buildBestSquadStatusSelector,
  setShowingBestSquad,
  showingBestSquadSelector,
} from "@repo/shared/redux";
import { formatExpiryAt, formatPrice, sbcSetPath } from "@repo/shared/utils";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ComponentProps, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { TryNextInsights, useTryNextInsights } from "./useTryNextInsights";

type CardId =
  | "import"
  | "showBest"
  | "setup"
  | "clubEvolution"
  | "profit"
  | "sbc"
  | "evolution"
  | "gallery"
  | "liveEvent"
  | "recommendations"
  | "budget"
  | "bestSquad";

type Card = {
  id: CardId;
  /** A design-system content tag; or, where none fits, a text label. */
  tag?: ComponentProps<typeof ContentTag>["contentType"];
  label?: "setup";
  /** Where the call to action goes; omitted, it is not live yet. */
  to?: string;
};

/*
  The onboarding: one step after another, so the user feels the club come
  together. Every step has general copy and a link to the page that does the
  thing; where the club gives us a figure (its best upgrade, its best
  evolution, the SBC it should do next), the step says that instead (see
  cardContent). Import is done by the time anyone sees this page.
*/
const STEPS: Card[] = [
  { id: "import", tag: "club" },
  // Its "Show" builds the best squad and puts it on the pitch: see useTryNext.
  { id: "showBest", tag: "club" },
  { id: "recommendations", tag: "market", to: "/my-club/recommendations" },
  { id: "sbc", tag: "sbc", to: "/sbc?sort=most-upvoted" },
  { id: "gallery", tag: "gallery", to: "/fut-gallery" },
  // The squad builder with the current live event picked, club players only.
  {
    id: "liveEvent",
    tag: "squadBuilder",
    to: "/squad-builder?customize=1&scope=club&liveEvent=1",
  },
  // Left out for now.
  // {
  //   id: "evolution",
  //   tag: "evolution",
  //   to: "/evolutions/players?sort=upgrade&activeSquad",
  // },
  // Its budget is the club's own: see cardContent.
  { id: "budget", tag: "squadBuilder", to: "/squad-builder" },
  { id: "profit", tag: "club", to: "/my-club/players?sort=profit" },
  // Left out for now; its CTA opens the setup dialog: see useTryNext.
  // { id: "setup", label: "setup" },
];

type CardContent = {
  /** Overrides the card's own tag, e.g. the upgrade's kind (SBC, Market). */
  tag?: Card["tag"];
  title: string;
  body: ReactNode;
  cta: string;
  to?: string;
  /** Top right, beside the tag: the card's figure. */
  figure?: ReactNode;
  /** Left of the text: the player(s) the card is about. */
  media?: ReactNode;
};

type Translate = ReturnType<typeof useTranslation>["t"];

/* A player card small enough to sit beside two lines of text. */
const MEDIA_SCALE = 0.3;

const MiniCard = ({
  player,
}: {
  player: { cardName?: string; name?: string };
}) => (
  <PlayerCardCondensed
    player={
      {
        ...player,
        cardName: player.cardName || player.name || "",
      } as ComponentProps<typeof PlayerCardCondensed>["player"]
    }
    scale={MEDIA_SCALE}
    hidePrice
  />
);

/** The design system's meta rating chip, with the rise beside the rating. */
const GainChip = ({
  metaRating,
  delta,
  playerRoleId,
}: {
  metaRating: number;
  delta: number;
  /** For the tier badge; without it the chip shows no tier. */
  playerRoleId?: number;
}) => {
  const tier = useSelector(
    tierSelector(
      playerRoleId != null ? metaRating : null,
      playerRoleId ?? null,
      true,
    ),
  );
  return (
    <MetaRatingChip
      metaRating={metaRating}
      metaRatingDelta={delta}
      tier={tier ?? undefined}
    />
  );
};

/* A rise in the card's text: green, behind the upgrade arrow. */
const Up = ({ children }: { children?: ReactNode }) => (
  // Inline, not flex, so the number sits on the sentence's baseline. The
  // arrow is an inline, text-sized image: its bottom lands on the baseline,
  // level with the bottom of the letters.
  <span className="font-semibold whitespace-nowrap text-upgrade">
    <span className="[&>svg]:inline [&>svg]:size-3 [&>svg]:align-baseline">
      <MetaRatingUpgradeArrow />
    </span>
    {children}
  </span>
);

/** What a card says: its general copy, or the club's own figure if known. */
const cardContent = (
  card: Card,
  insights: TryNextInsights,
  t: Translate,
): CardContent => {
  const { upgrade, evolution, clubEvolution, sbc, budget } = insights;
  // Only the budget card's copy has a figure in it.
  const values = { budget: formatPrice({ price: budget, compact: true }) };
  const general: CardContent = {
    title: t(`home.tryNext.cards.${card.id}.title`, values),
    body: t(`home.tryNext.cards.${card.id}.body`, values),
    cta: card.id === "import" ? "" : t(`home.tryNext.cards.${card.id}.cta`),
    to: card.to,
  };

  if (card.id === "budget") {
    return {
      ...general,
      to: `/squad-builder?customize=1&budget=${budget}&scope=both`,
    };
  }

  if (
    card.id === "recommendations" &&
    !insights.recsStale &&
    upgrade?.rec.newPlayer
  ) {
    const { rec, delta, playerDelta, slotMeta, slotRoleId } = upgrade;
    const name = rec.newPlayer.cardName ?? "";
    return {
      ...general,
      // The upgrade's own kind, as on the recommendations page.
      tag: rec.type,
      title: t("home.tryNext.live.recommendations.title", { name }),
      body: (
        <Trans
          ns="myClub"
          i18nKey={
            playerDelta > 0
              ? "home.tryNext.live.recommendations.body"
              : "home.tryNext.live.recommendations.bodySquadOnly"
          }
          values={{ squad: delta.toFixed(1), player: playerDelta.toFixed(1) }}
          components={{ up: <Up /> }}
        />
      ),
      cta: t("home.tryNext.live.recommendations.cta"),
      // The player's meta rating in that slot once swapped in, with its rise.
      figure:
        slotMeta != null && playerDelta > 0 ? (
          <GainChip
            metaRating={slotMeta + playerDelta}
            delta={playerDelta}
            playerRoleId={slotRoleId}
          />
        ) : undefined,
      media: (
        <div className="flex items-center gap-1">
          {rec.oldPlayer?.resourceId ? (
            <MiniCard player={rec.oldPlayer} />
          ) : (
            rec.oldPlayer && <EmptySlotCard scale={MEDIA_SCALE} />
          )}
          {rec.oldPlayer && (
            <ArrowRight className="size-3 flex-none text-neutral-400" />
          )}
          <MiniCard player={rec.newPlayer} />
        </div>
      ),
    };
  }

  if (card.id === "evolution" && evolution) {
    const { player, delta } = evolution;
    const name = player.cardName || player.name;
    return {
      ...general,
      title: t("home.tryNext.live.evolution.title", { name }),
      body: t("home.tryNext.live.evolution.body", {
        name,
        delta: delta.toFixed(1),
      }),
      cta: t("home.tryNext.live.evolution.cta"),
      figure: (
        <GainChip
          metaRating={player.metaRating}
          delta={delta}
          playerRoleId={player.playerRoleId}
        />
      ),
      media: <MiniCard player={player} />,
    };
  }

  if (card.id === "sbc" && sbc) {
    const marquee = MARQUEE.test(sbc.name);
    const rewardName = sbc.reward?.name || sbc.reward?.shortName;
    const expiresIn = formatExpiryAt(sbc.endTime);
    return {
      ...general,
      title: sbc.name,
      // A marquee rewards a pack: say which.
      body:
        marquee && rewardName
          ? t("home.tryNext.live.sbc.marqueeBodyReward", { reward: rewardName })
          : t(
              marquee
                ? "home.tryNext.live.sbc.marqueeBody"
                : "home.tryNext.live.sbc.body",
            ),
      cta: t("home.tryNext.live.sbc.cta"),
      to: sbcSetPath(sbc),
      // When it ends, which is what decides whether to do it now.
      figure: expiresIn ? (
        <span className="text-xs text-foreground-muted">
          {t("sbcs:setCard.expiresIn", { time: expiresIn })}
        </span>
      ) : undefined,
      media: sbc.reward?.player ? (
        <MiniCard player={sbc.reward.player} />
      ) : sbc.reward ? (
        // A pack, as the SBC pages show it, the height of a mini card.
        <img
          loading="lazy"
          className="h-16 w-auto drop-shadow-md"
          alt={rewardName ?? ""}
          src={extractRewardUrl(sbc.reward)}
        />
      ) : undefined,
    };
  }

  if (card.id === "clubEvolution" && clubEvolution) {
    const { player, delta } = clubEvolution;
    const name = player.cardName || player.name;
    return {
      ...general,
      title: t("home.tryNext.live.clubEvolution.title", { name }),
      body: t("home.tryNext.live.clubEvolution.body", {
        name,
        meta: player.metaRating.toFixed(1),
      }),
      figure: (
        <GainChip
          metaRating={player.metaRating}
          delta={delta}
          playerRoleId={player.playerRoleId}
        />
      ),
      media: <MiniCard player={player} />,
    };
  }

  // Recommendations that no longer fit the squad: say so, and send the user
  // to update them, rather than show an upgrade that may be wrong.
  if (card.id === "recommendations" && insights.recsStale) {
    return {
      ...general,
      title: t("home.tryNext.live.recommendations.staleTitle"),
      body: t("home.tryNext.live.recommendations.staleBody"),
      cta: t("home.tryNext.live.recommendations.staleCta"),
    };
  }

  return general;
};

/*
  Once every card above is put away, these stay: no OK, always about the club
  as it is now.
*/
const PERMANENT: Card[] = [
  { id: "sbc", tag: "sbc", to: "/sbc?sort=most-upvoted" },
  { id: "recommendations", tag: "market", to: "/my-club/recommendations" },
  {
    id: "clubEvolution",
    tag: "evolution",
    to: "/evolutions/players?sort=meta-rating&ownedOrEvolved=Owned",
  },
  // Best formation on an empty pitch, from the club only.
  {
    id: "bestSquad",
    tag: "squadBuilder",
    to: "/squad-builder?customize=1&scope=club&bestFormation=1",
  },
];

const MARQUEE = /marquee matchups/i;

/**
 * The head of the club home's side panel: "For your club", and while the
 * onboarding runs, how far along it is.
 */
export const TryNextHeader = ({
  done,
  total,
  showProgress = true,
}: {
  done: number;
  total: number;
  /** Off once the onboarding was already done when the page opened. */
  showProgress?: boolean;
}) => {
  const { t } = useTranslation("myClub");
  const finished = done >= total;
  return (
    <div className="flex flex-col gap-3">
      {/* Onboarding while steps are left; "For your club" once they're done. */}
      <div className="flex flex-col gap-1">
        <h2 className="font-oswald text-2xl leading-tight font-semibold tracking-wide uppercase">
          {finished ? t("home.tryNext.title") : t("home.tryNext.steps.title")}
        </h2>
        <span className="text-sm text-neutral-300">
          {finished
            ? t("home.tryNext.subtitle")
            : t("home.tryNext.steps.subtitle")}
        </span>
      </div>
      {showProgress && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-neutral-300">
            {finished
              ? t("home.tryNext.steps.allDone", { total })
              : t("home.tryNext.steps.progress", { step: done, total })}
          </span>
          <ProgressBar ratio={done / total} small />
        </div>
      )}
    </div>
  );
};

type StepState = "done" | "next" | "todo";

/**
 * One step. The next one is lit and its button is the primary; a done one
 * folds down to its title, crossed out, with a tick where the others have
 * "Mark done". Every step can still be opened again.
 */
const StepCard = ({
  card,
  content,
  state,
  busy = false,
  onAction,
  onToggleDone,
  style,
  className,
}: {
  card: Card;
  content: CardContent;
  /** Omitted for the permanent cards after the onboarding: no tick. */
  state?: StepState;
  /** The step's own action is running, e.g. the best squad building. */
  busy?: boolean;
  onAction: () => void;
  onToggleDone?: () => void;
  style?: React.CSSProperties;
  className?: string;
}) => {
  const { t } = useTranslation("myClub");
  const { label } = card;
  const tag = content.tag ?? card.tag;
  const { title, body, cta, to, figure, media } = content;
  const done = state === "done";
  const next = state === "next";

  return (
    <article
      style={style}
      aria-current={next ? "step" : undefined}
      className={cn(
        "flex flex-col gap-3 rounded-xl border p-4 transition-[color,background-color,border-color,opacity]",
        next
          ? "border-blue-800/70 bg-blue-950/40"
          : "border-neutral-700 bg-neutral-800",
        // Done recedes: still there to reopen, no longer asking for anything.
        done &&
          "gap-2 border-neutral-800 bg-neutral-900/60 py-3 opacity-50 hover:opacity-80",
        className,
      )}
    >
      {/* As tall as a tag, so a text label keeps the cards level. */}
      <div className="flex min-h-6 items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {tag ? (
            <ContentTag contentType={tag} />
          ) : (
            <span className="text-xs font-semibold tracking-wider text-neutral-300 uppercase">
              {t(`home.tryNext.labels.${label ?? "setup"}`)}
            </span>
          )}
          {next && (
            <span className="text-xs font-semibold tracking-wider text-blue-300 uppercase">
              {t("home.tryNext.steps.nextUp")}
            </span>
          )}
        </div>
        {!done && figure}
      </div>
      {done ? (
        <h3 className="text-sm leading-snug font-semibold text-neutral-400 line-through decoration-neutral-500">
          {title}
        </h3>
      ) : (
        <div className="flex items-center gap-3">
          {media && <div className="flex-none">{media}</div>}
          <div className="flex min-w-0 flex-col gap-1">
            <h3 className="text-sm leading-snug font-semibold text-neutral-50">
              {title}
            </h3>
            <p className="text-xs leading-snug text-foreground-muted">{body}</p>
          </div>
        </div>
      )}
      <div className="mt-auto flex items-center justify-between gap-3">
        {state && onToggleDone ? (
          // A tick and a word, not a button: the user says when it's done.
          <label className="relative -ml-0.5 inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-neutral-300 select-none hover:text-neutral-50">
            <input
              type="checkbox"
              checked={done}
              onChange={onToggleDone}
              className="peer sr-only"
            />
            <CheckBox
              checked={done}
              className={cn(
                !done && "border-neutral-500",
                "peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400",
              )}
            />
            <span>
              {done
                ? t("home.tryNext.steps.done")
                : t("home.tryNext.steps.markDone")}
            </span>
          </label>
        ) : (
          <span />
        )}
        {cta && (
          <Button
            variant={next ? "primary" : done ? "subtle" : "secondary"}
            size="small"
            disabled={
              busy || (!to && card.id !== "showBest" && card.id !== "setup")
            }
            onClick={onAction}
          >
            {busy ? t("home.tryNext.steps.building") : cta}
          </Button>
        )}
      </div>
    </article>
  );
};

/**
 * "For your club" as an onboarding: eight steps, from the import (done) to
 * setting the club up, each ticked off by the user.
 * Only the user completes a step, by ticking it; doing it is not enough.
 * Steps done are kept on this browser for now. Once all are done, the
 * permanent cards take their place.
 */
export const useTryNext = () => {
  const { t } = useTranslation(["myClub", "sbcs", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const insights = useTryNextInsights(true);
  const buildStatus = useSelector(buildBestSquadStatusSelector);
  const bestSquad = useSelector(bestSquadSelector);
  const [setupOpen, setSetupOpen] = useState(false);
  const { user, status: userStatus } = useSelector(getUserSelector);

  // The import is behind the user: it is why this page is theirs.
  const isDone = (id: CardId) => {
    const step =
      ONBOARDING_STEP_FEATURES[id as keyof typeof ONBOARDING_STEP_FEATURES];
    return !step || !!user?.usedFeatures?.includes(step);
  };
  const toggleDone = (id: CardId) => {
    const step =
      ONBOARDING_STEP_FEATURES[id as keyof typeof ONBOARDING_STEP_FEATURES];
    if (step) dispatch(setOnboardingStep({ step, done: !isDone(id) }));
  };

  const showing = useSelector(showingBestSquadSelector);

  const build = () =>
    dispatch(buildBestSquad())
      .unwrap()
      .then(() => true)
      .catch(() => false);

  /*
    Show works like the old Active | Best toggle: the best squad on the pitch,
    or the user's own back. It is built on the first Show, not before, so
    nothing runs for a user who never asks; the first build is free and
    the backend ticks the step, which the page picks up on its next load. On a narrow screen the pitch
    is above, so it is brought into view.
  */
  const showBest = async () => {
    if (showing) return dispatch(setShowingBestSquad(false));
    if (!bestSquad && !(await build())) {
      dispatch(
        addAlert({ type: "error", description: t("common:error.generic") }),
      );
      return;
    }
    dispatch(setShowingBestSquad(true));
    if (isTouch)
      document
        .getElementById("active-squad")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const act = (card: Card, content: CardContent) => {
    if (card.id === "showBest") return showBest();
    if (card.id === "setup") return setSetupOpen(true);
    if (content.to) navigate(content.to);
  };

  const doneCount = STEPS.filter((card) => isDone(card.id)).length;
  const finished = doneCount === STEPS.length;
  /*
    "All 9 steps done" is for the visit the last step is ticked in: once the
    page has seen a step left to do, the bar stays. Opened already finished
    (a reload, a later visit), the panel is just "For your club".
  */
  const [sawUnfinished, setSawUnfinished] = useState(false);
  // Only once the profile is in: before it, every step looks undone.
  const profileLoaded = userStatus === "fulfilled";
  useEffect(() => {
    if (profileLoaded && !finished) setSawUnfinished(true);
  }, [profileLoaded, finished]);

  /*
    Ticking the last step ends the onboarding with a moment of its own, and
    says where things are from here on: ideas on the right, the rest of My
    Club on the left.
  */
  const [celebrating, setCelebrating] = useState(false);
  useEffect(() => {
    if (finished && sawUnfinished) setCelebrating(true);
  }, [finished, sawUnfinished]);
  const nextId = STEPS.find((card) => !isDone(card.id))?.id;

  /*
    On touch the steps are a sideways row, read from its start: the ones left
    to do lead, in order, and the done ones wait at the back, so the row
    opens on the next step instead of a crossed-out one.
  */
  const stepsForRow = [
    ...STEPS.filter((card) => !isDone(card.id)),
    ...STEPS.filter((card) => isDone(card.id)),
  ];

  const contentFor = (card: Card) => {
    let content = cardContent(card, insights, t);
    // While it is on the pitch, the step's button takes it off again.
    if (card.id === "showBest" && showing)
      content = { ...content, cta: t("home.squad.backToActive") };
    // Once built, the button only shows it again.
    else if (card.id === "showBest" && bestSquad)
      content = { ...content, cta: t("home.tryNext.cards.showBest.ctaBuilt") };
    // The best squad's figure: how much better it is, once built.
    if (card.id === "showBest" && insights.bestGain != null && bestSquad)
      return {
        ...content,
        figure: (
          <MetaRatingChip
            metaRating={insights.bestGain.metaRating}
            metaRatingDelta={insights.bestGain.delta}
          />
        ),
      };
    return content;
  };

  const cards = finished
    ? PERMANENT.map((card) => {
        const content = cardContent(card, insights, t);
        return (
          <StepCard
            key={card.id}
            card={card}
            content={content}
            onAction={() => content.to && navigate(content.to)}
          />
        );
      })
    : (isTouch ? stepsForRow : STEPS).map((card) => {
        const content = contentFor(card);
        return (
          <StepCard
            key={card.id}
            card={card}
            content={content}
            state={
              isDone(card.id) ? "done" : card.id === nextId ? "next" : "todo"
            }
            busy={card.id === "showBest" && buildStatus === "pending"}
            onAction={() => act(card, content)}
            // The import can't be undone from here.
            onToggleDone={
              card.id === "import" ? undefined : () => toggleDone(card.id)
            }
          />
        );
      });

  return {
    cards: (
      <>
        {cards}
        <CelebrationDialog
          isOpen={celebrating}
          onClose={() => setCelebrating(false)}
          title={t("home.tryNext.finished.title")}
          subtitle={t("home.tryNext.finished.subtitle")}
          action={
            <Button
              variant="primary"
              size="medium"
              onClick={() => setCelebrating(false)}
            >
              {t("home.tryNext.finished.cta")}
            </Button>
          }
        >
          <ul className="flex flex-col gap-2.5 text-left text-sm text-neutral-200">
            <li className="flex items-center gap-2.5">
              <ArrowRight className="size-4 shrink-0 text-streamline-success" />
              {t("home.tryNext.finished.right")}
            </li>
            <li className="flex items-center gap-2.5">
              <ArrowLeft className="size-4 shrink-0 text-streamline-success" />
              {t("home.tryNext.finished.left")}
            </li>
          </ul>
        </CelebrationDialog>
        {/* Portalled: the panel is its own stacking context. */}
        {createPortal(
          <SetupDialog
            isOpen={setupOpen}
            onClose={() => setSetupOpen(false)}
          />,
          document.body,
        )}
      </>
    ),
    done: finished ? STEPS.length : doneCount,
    total: STEPS.length,
    showProgress: !finished || sawUnfinished,
  };
};
